import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { InvoiceLineKind, InvoiceStatus } from "@api/shared-runtime/enums";
import {
  describePaidWith,
  describeRefund,
} from "@api/shared-runtime/billing-collection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatInvoicePeriod,
  formatPlanCurrency,
  formatPlanInstant,
} from "@/components/billing-plan/billing-plan-values";
import { useTRPC } from "@/trpc";
import { InvoiceDetailSheet } from "./invoice-detail-sheet";
import { PayInvoiceLink } from "./pay-invoice-link";
import { PayInvoiceButton, type PayableAccount } from "./pay-invoice-button";
import {
  BILLING_CADENCE_DESCRIPTIONS,
  CUSTOMER_INVOICE_STATUS_LABELS,
  PAYMENT_FAILED_NOTICE,
  PAYMENT_PENDING_LABEL,
  PAYMENT_PENDING_NOTICE,
  type CustomerInvoice,
} from "./invoice-presentation";

/** Base fee period first; an in-advance invoice also names the usage month it carries. */
function describeCoverage(invoice: CustomerInvoice): string {
  const cycle = formatInvoicePeriod({
    start: invoice.cycleStart,
    end: invoice.cycleEnd,
  });
  if (!invoice.usagePeriodStart || !invoice.usagePeriodEnd) {
    return cycle;
  }
  const usage = formatInvoicePeriod({
    start: invoice.usagePeriodStart,
    end: invoice.usagePeriodEnd,
  });
  if (usage === cycle) {
    return cycle;
  }
  if (invoice.lines.every((line) => line.kind !== InvoiceLineKind.BaseFee)) {
    return `Usage ${usage}`;
  }
  return `${cycle} + usage ${usage}`;
}

function formatInvoiceTotal(invoice: CustomerInvoice): string {
  return formatPlanCurrency(invoice.totalAmount ?? invoice.subtotalAmount);
}

/** Nothing is due while a direct charge is settling. */
function formatDueDate(invoice: CustomerInvoice): string {
  if (
    invoice.status !== InvoiceStatus.Open ||
    invoice.paymentPending ||
    !invoice.dueAt
  ) {
    return "—";
  }
  return formatPlanInstant(invoice.dueAt);
}

/** What the customer should know at a glance beyond the status badge. */
function describePayment(invoice: CustomerInvoice): string | null {
  const refund = describeRefund(invoice);
  if (refund) {
    return refund;
  }
  const paidWith = describePaidWith(invoice);
  if (paidWith) {
    return `Paid with ${paidWith}`;
  }
  if (invoice.paymentPending) {
    return PAYMENT_PENDING_NOTICE;
  }
  if (invoice.paymentFailed && invoice.status === InvoiceStatus.Open) {
    return PAYMENT_FAILED_NOTICE;
  }
  return null;
}

function StatusBadge({ invoice }: { invoice: CustomerInvoice }) {
  if (invoice.paymentPending) {
    return (
      <Badge variant="secondary" className="whitespace-nowrap">
        {PAYMENT_PENDING_LABEL}
      </Badge>
    );
  }
  return (
    <Badge
      variant={invoice.status === InvoiceStatus.Paid ? "default" : "outline"}
      className="whitespace-nowrap"
    >
      {CUSTOMER_INVOICE_STATUS_LABELS[invoice.status]}
    </Badge>
  );
}

export function InvoicesCard({
  organizationBusinessUnitId,
  unitName,
  canManage,
  account,
}: {
  organizationBusinessUnitId: string;
  unitName: string;
  canManage: boolean;
  account: PayableAccount | null;
}) {
  const trpc = useTRPC();
  const invoicesQuery = useQuery(
    trpc.organization.listInvoices.queryOptions({ organizationBusinessUnitId }),
  );
  const [viewingId, setViewingId] = useState<string | null>(null);
  const data = invoicesQuery.data;
  const viewing = data?.invoices.find((row) => row.id === viewingId) ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
        <CardDescription>
          Monthly invoices for {unitName}.
          {data?.baseFeeTiming
            ? ` ${BILLING_CADENCE_DESCRIPTIONS[data.baseFeeTiming]}`
            : ""}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!data ? (
          <Skeleton className="h-16 w-full" />
        ) : data.invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">No invoices yet.</p>
        ) : (
          <>
            <ul className="divide-y md:hidden">
              {data.invoices.map((invoice) => (
                <li key={invoice.id} className="space-y-2 py-3 first:pt-0">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-medium">
                      {describeCoverage(invoice)}
                    </p>
                    <StatusBadge invoice={invoice} />
                  </div>
                  <dl className="flex justify-between gap-3 text-sm">
                    <div>
                      <dt className="text-muted-foreground">Total</dt>
                      <dd className="tabular-nums">
                        {formatInvoiceTotal(invoice)}
                      </dd>
                    </div>
                    <div className="text-right">
                      <dt className="text-muted-foreground">Due</dt>
                      <dd>{formatDueDate(invoice)}</dd>
                    </div>
                  </dl>
                  {describePayment(invoice) && (
                    <p className="text-xs text-muted-foreground">
                      {describePayment(invoice)}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <PayInvoiceLink invoice={invoice} size="sm" />
                    {canManage && (
                      <PayInvoiceButton
                        invoice={invoice}
                        organizationBusinessUnitId={organizationBusinessUnitId}
                        account={account}
                        size="sm"
                      />
                    )}
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0"
                      onClick={() => setViewingId(invoice.id)}
                    >
                      View details
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow>
                  <TableHead>Covers</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{describeCoverage(invoice)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge invoice={invoice} />
                        {describePayment(invoice) && (
                          <span className="text-xs text-muted-foreground">
                            {describePayment(invoice)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatInvoiceTotal(invoice)}
                    </TableCell>
                    <TableCell>{formatDueDate(invoice)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <PayInvoiceLink invoice={invoice} size="sm" />
                        {canManage && (
                          <PayInvoiceButton
                            invoice={invoice}
                            organizationBusinessUnitId={
                              organizationBusinessUnitId
                            }
                            account={account}
                            size="sm"
                          />
                        )}
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto p-0"
                          onClick={() => setViewingId(invoice.id)}
                        >
                          View details
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
      <InvoiceDetailSheet
        invoice={viewing}
        organizationBusinessUnitId={organizationBusinessUnitId}
        canManage={canManage}
        account={account}
        onOpenChange={(open) => !open && setViewingId(null)}
      />
    </Card>
  );
}
